import { useCallback } from 'react';
import { useUndoDocumentMutation, useRedoDocumentMutation, useGetDocumentHistoryQuery } from '@/api/undoRedo';

interface UseUndoRedoProps {
  documentId?: string;
}

interface UseUndoRedoReturn {
  canUndo: boolean;
  canRedo: boolean;
  isUndoing: boolean;
  isRedoing: boolean;
  handleUndo: () => Promise<void>;
  handleRedo: () => Promise<void>;
  refetchHistory: () => void;
}

export const useUndoRedo = ({ documentId }: UseUndoRedoProps): UseUndoRedoReturn => {
  // Get document history to determine button states
  const { data: historyData, refetch: refetchHistory } = useGetDocumentHistoryQuery(
    { documentId: documentId! },
    {
      skip: !documentId,
      refetchOnMountOrArgChange: true,
    },
  );

  // Undo/Redo mutations
  const [undoDocument, { isLoading: isUndoing }] = useUndoDocumentMutation();
  const [redoDocument, { isLoading: isRedoing }] = useRedoDocumentMutation();

  // Determine if undo/redo actions are available
  const canUndo = (historyData?.applied ?? 0) > 0;
  const canRedo = (historyData?.undone ?? 0) > 0;

  // Handle undo action
  const handleUndo = useCallback(async () => {
    if (!documentId || !canUndo) {
      return;
    }

    try {
      const result = await undoDocument({ documentId }).unwrap();

      if (result.success) {
        // Refetch history to update button states
        refetchHistory();
      }
    } catch (error) {
      console.error('Failed to undo:', error);
    }
  }, [documentId, canUndo, undoDocument, refetchHistory]);

  // Handle redo action
  const handleRedo = useCallback(async () => {
    if (!documentId || !canRedo) {
      return;
    }

    try {
      const result = await redoDocument({ documentId }).unwrap();

      if (result.success) {
        // Refetch history to update button states
        refetchHistory();
      }
    } catch (error) {
      console.error('Failed to redo:', error);
    }
  }, [documentId, canRedo, redoDocument, refetchHistory]);

  return {
    canUndo,
    canRedo,
    isUndoing,
    isRedoing,
    handleUndo,
    handleRedo,
    refetchHistory,
  };
};
